/**
 * PDF block view wrapper used to customize `@eeacms/volto-pdf-block`.
 *
 * What changed vs upstream:
 * - Migrated to TypeScript types (removed `PropTypes`).
 * - Forces `fitPageWidth` off; mobile responsiveness is handled by our local `PDFViewer` auto-fit.
 */

import React from 'react';
import loadable from '@loadable/component';
import { Button } from 'semantic-ui-react';
import { Icon } from '@plone/volto/components';

import config from '@plone/volto/registry';
import { flattenToAppURL } from '@plone/volto/helpers';

import NavigationToolbar from '@eeacms/volto-pdf-block/components/manage/PDFViewer/PDFNavigation';

import { urlToCorsProxy } from '@eeacms/volto-pdf-block/components/helpers';

import downloadSVG from '@plone/volto/icons/move-down.svg';

import '@eeacms/volto-pdf-block/components/manage/PDFViewer/pdf-styling.css';

import type { PDFViewerProps } from './PDFViewer';

const LoadablePDFViewer = loadable(() => import('./PDFViewer'), {
  fallback: () => <div>Loading PDF file...</div>,
  ssr: false,
}) as React.ComponentType<PDFViewerProps>;
const CSS_UNITS = 96 / 72;

type PDFViewport = {
  width: number;
  height: number;
};

const downloadUrl = (url: string) =>
  url.indexOf('@@download') === -1
    ? `${flattenToAppURL(url)}/@@download/file`
    : url;

type DownloadOverlayProps = {
  url: string;
  size: [string, string];
};

const DownloadOverlay = ({ url, size }: DownloadOverlayProps) => {
  return (
    <div
      className="pdf-block-download-overlay"
      style={{
        width: size[0],
        height: size[1],
        position: 'absolute',
        background: 'rgb(204, 204, 204, 0.5)',
        zIndex: 2,
      }}
    >
      <div className="icon-wrapper">
        <span className="btn-icon">
          <Button circular compact primary as="a" href={url} alt="Download">
            <Icon name={downloadSVG} size="18px" />
          </Button>
        </span>
      </div>
    </div>
  );
};

export type PDFBlockData = {
  url?: string;
  clickToDownload?: boolean;
  hideToolbar?: boolean;
  hideNavbar?: boolean;
  showPagesPreview?: boolean;
  initialPage?: number | string;
  initial_scale?: number;
  initial_scale_ratio?: number;
  disableScroll?: boolean;
};

export type PDFBlockViewProps = {
  data: PDFBlockData;
};

const PDFBlockView = ({ data }: PDFBlockViewProps) => {
  const dataUrl =
    (data.url &&
      (data.url.includes(config.settings.apiPath) || data.url.startsWith('/')
        ? downloadUrl(data.url)
        : urlToCorsProxy(data.url))) ||
    null;
  const [size, setSize] = React.useState<[string, string] | undefined>();

  return (
    <div className="pdf-viewer-block">
      {data.clickToDownload && size && (
        <DownloadOverlay url={dataUrl} size={size} />
      )}
      {dataUrl && (
        <LoadablePDFViewer
          document={{
            url: dataUrl,
          }}
          css="pdf-viewer"
          navigation={NavigationToolbar}
          showToolbar={!data.hideToolbar}
          showNavbar={!data.hideNavbar}
          showPagesPreview={data.showPagesPreview}
          page={parseInt(String(data.initialPage || 1), 10)}
          initial_scale={data.initial_scale}
          initial_scale_ratio={data.initial_scale_ratio}
          enableScroll={!data.disableScroll}
          // Intencional: NÃO usar fitPageWidth aqui. O auto-fit mobile é feito
          // via ajuste inicial de `scale` no PDFViewer customizado.
          fitPageWidth={false}
          onPageRenderSuccess={(_page, _canvasEl, viewport: PDFViewport) => {
            setSize([
              `${Math.round(viewport.width / CSS_UNITS)}px`,
              `${Math.round(viewport.height / CSS_UNITS)}px`,
            ]);
          }}
        />
      )}
    </div>
  );
};

export default PDFBlockView;
